import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

import { UIAvatar, UISelect, UITextarea, UIModal } from "../../../../ui";

import "./AddProjectManager.scss";

const AddProjectManagerModal = ({
  open,
  labels,
  managers,
  selectedManagerIds,
  onClose,
  onAdd,
}) => {
  const [manager, setManager] = useState(null);
  const [responsibility, setResponsibility] = useState("");

  const availableManagers = useMemo(
    () =>
      managers.filter(
        (item) => !selectedManagerIds.includes(String(item.id)),
      ),
    [managers, selectedManagerIds],
  );
  const canSubmit = Boolean(manager && responsibility.trim());

  const handleClose = () => {
    setManager(null);
    setResponsibility("");
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    onAdd({
      ...manager,
      role: responsibility.trim(),
      responsibility: responsibility.trim(),
    });
    handleClose();
  };

  return (
    <UIModal
      open={open}
      type="info"
      title={labels.addProjectManagerTitle}
      cancelLabel={labels.cancel}
      confirmLabel={labels.confirmAddManager}
      onClose={handleClose}
      onCancel={handleClose}
      onConfirm={handleSubmit}
      confirmButtonProps={{
        disabled: !canSubmit,
        appearance: "gradient",
      }}
      PaperProps={{ className: "add-project-manager-modal" }}
      maxWidth="sm"
      fullWidth
    >
      <div className="add-project-manager-modal__fields">
        <p className="add-project-manager-modal__hint">{labels.managerHelper}</p>

        <UISelect
          name="projectManagerSearch"
          label={labels.managerName}
          placeholder={labels.managerNamePlaceholder}
          options={availableManagers}
          value={manager}
          required
          onChange={setManager}
          getOptionLabel={(option) =>
            option ? `${option.name}${option.email ? ` - ${option.email}` : ""}` : ""
          }
          isOptionEqualToValue={(option, selected) => option.id === selected?.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <div className="add-project-manager-modal__option">
                <UIAvatar
                  className="add-project-manager-modal__option-avatar"
                  src={option.profile_picture_url}
                  name={option.name}
                  initials={option.initials}
                  size="sm"
                  alt={option.name}
                />
                <div className="add-project-manager-modal__option-body">
                  <strong>{option.name}</strong>
                  <span>{option.email}</span>
                </div>
              </div>
            </li>
          )}
        />

        <UITextarea
          name="projectManagerResponsibility"
          label={labels.responsibility}
          placeholder={labels.responsibilityPlaceholder}
          value={responsibility}
          required
          minRows={4}
          onValueChange={setResponsibility}
        />
      </div>
    </UIModal>
  );
};

AddProjectManagerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  labels: PropTypes.object.isRequired,
  managers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string,
      initials: PropTypes.string,
      profile_picture_url: PropTypes.string,
      role: PropTypes.string,
    }),
  ).isRequired,
  selectedManagerIds: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

AddProjectManagerModal.defaultProps = {
  selectedManagerIds: [],
};

export default AddProjectManagerModal;
